<script setup lang="ts">
import {computed, onMounted, ref, Ref} from "vue";
import pulseBackend from "@/backend/pulse.backend";
import WeekPicker from "@/components/WeekPicker.vue";
import {DateTime} from "luxon";
import {useRoute, useRouter} from "vue-router";
import {CompanyPulseData} from "@/types/DataTypes.ts";
import ExpandablePersonEntry from "./ExpandablePersonEntry.vue";
import CompanyWeekAllocationBar from "./CompanyWeekAllocationBar.vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import type {Team} from '@/backend/teams.backend';

interface Props {
  team: Team;
}

const {team} = defineProps<Props>();
const router = useRouter();
const route = useRoute();

const loading = ref(false);
const weekStart: Ref<DateTime> = ref<DateTime>(initWeek());
const pulses = ref<CompanyPulseData[] | null>();
const filter = ref();

const filtered = computed(() => {
  if (!pulses.value) return null;
  
  // Filter by team members
  const teamUserIds = team.users?.map(u => u.id) || [];
  const teamPulses = pulses.value.filter(p => teamUserIds.includes(p.user.id));
  
  // Apply text filter
  if (!filter.value) return teamPulses;
  
  const term = filter.value.toLowerCase();
  return teamPulses.filter((p) => {
    return p.user.name.toLowerCase().includes(term) ||
      (p.user.department?.name && p.user.department.name.toLowerCase().includes(term));
  });
});

function initWeek() {
  const baseDate = route.query.week ? DateTime.fromISO(route.query.week as string) : DateTime.now();
  return baseDate.startOf('day').startOf('week');
}

async function loadPulses() {
  loading.value = true;
  const isoDate = weekStart.value.toISODate();
  pulses.value = await pulseBackend.week(isoDate!)();
  loading.value = false;
}

onMounted(async () => {
  const weekOf = route.query.week ? DateTime.fromISO(route.query.week as string) : DateTime.now().startOf('day').startOf('week');
  if (weekOf.toISODate() !== weekStart.value.toISODate()) {
    weekStart.value = weekOf;
    pulses.value = null;
  }

  if (!pulses.value) {
    await loadPulses();
  }
});

async function switchWeek() {
  await router.push({query: {week: weekStart.value.toISODate()}});
  await loadPulses();
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <WeekPicker v-model="weekStart" @change="switchWeek"/>
      <IconField>
        <InputIcon><i class="pi pi-search"/></InputIcon>
        <InputText v-model="filter" placeholder="Filter Names & Functions"/>
      </IconField>
    </div>

    <!-- Team allocation bar -->
    <div v-if="filtered && filtered.length > 0" class="border border-surface-200 dark:border-surface-700 rounded-lg p-4 bg-surface">
      <div class="flex items-center gap-4">
        <div class="flex-grow">
          <CompanyWeekAllocationBar :pulses="filtered" />
        </div>
      </div>
    </div>

    <!-- Team members list -->
    <div v-if="filtered && filtered.length > 0" class="flex flex-col gap-2">
      <ExpandablePersonEntry
        v-for="pulse in filtered"
        :key="pulse.user.id"
        :pulse="pulse"
      />
    </div>

    <!-- Empty state -->
    <div v-else-if="!loading && filtered" class="text-center text-gray-500 p-8">
      <i class="pi pi-inbox text-4xl mb-4 block"></i>
      <p>No pulse data available for this team this week</p>
    </div>
  </div>
</template>
